import { useMemo, useState } from 'react'
import {
  calculateDemoProductTotalCost,
  calculateDemoSemiFinishedTotalCost,
  calculateRecursiveUnitCost,
  isSemiFinishedEligibleForRecipeReport,
} from '../costUtils'
import {
  Category,
  DemoProduct,
  DemoSemiFinishedProduct,
  Packaging,
  Product,
  RawMaterial,
  SemiFinishedProduct,
  TradingGood,
} from '../types'

interface Props {
  product: Product | SemiFinishedProduct | DemoProduct | DemoSemiFinishedProduct
  productType: 'product' | 'semi_finished' | 'demo_product' | 'demo_semi_finished'
  onBack: () => void
  onOpenReport?: (
    item: Product | SemiFinishedProduct | DemoProduct | DemoSemiFinishedProduct | RawMaterial,
    type: 'product' | 'semi_finished' | 'demo_product' | 'demo_semi_finished' | 'raw_material'
  ) => void
  categories: Category[]
  productsMap: Map<string, Product>
  semiFinishedMap: Map<string, SemiFinishedProduct>
  rawMaterialsMap: Map<string, RawMaterial>
  packagingsMap: Map<string, Packaging>
  demoProductsMap?: Map<string, DemoProduct>
  demoSemiFinishedMap?: Map<string, DemoSemiFinishedProduct>
  tradingGoodsMap?: Map<string, TradingGood>
}

type ReportTab = 'recipe'

interface ColumnDef {
  key: string
  label: string
}

interface BomPrintRow {
  id: string
  depth: number
  code: string
  name: string
  typeLabel: string
  quantity: number
  unit: string
  totalCost: number
  isNode: boolean
}

function formatNumber(val: number, maxDecimals: number = 4): string {
  if (isNaN(val) || !isFinite(val)) return '0'
  return val.toLocaleString('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  })
}

function formatPrintQuantity(val: number): string {
  if (isNaN(val) || !isFinite(val)) return '0,00'
  return val.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })
}

function formatCurrency(val: number): string {
  if (isNaN(val) || !isFinite(val)) return '0,00'
  return val.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatPercent(val: number): string {
  if (isNaN(val) || !isFinite(val)) return '%0,00'
  return `%${val.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function ArrowLeftIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }}
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  )
}

function PieChartIcon({ size = 15, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px', ...style }}
    >
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  )
}

function PrinterIcon({ size = 15, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px', ...style }}
    >
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  )
}

function GearIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

export function ProductReportsView({
  product,
  productType,
  onBack,
  onOpenReport,
  categories,
  productsMap,
  semiFinishedMap,
  rawMaterialsMap,
  packagingsMap,
  demoProductsMap,
  demoSemiFinishedMap,
  tradingGoodsMap = new Map(),
}: Props) {
  const [activeTab, setActiveTab] = useState<ReportTab>('recipe')
  const [showColumnModal, setShowColumnModal] = useState<boolean>(false)
  const [hiddenColumns, setHiddenColumns] = useState<Record<string, boolean>>({})

  const n = Number(product.recipe_output_quantity || 1)
  const productUnit = product.unit || 'Adet'
  const components = product.components || []

  // Pre-calculate per-component values
  const componentRows = useMemo(() => {
    return components.map((comp: any) => {
      const compUnit = comp.component_unit || comp.unit || 'Adet'
      const isKg = compUnit.trim().toLowerCase() === 'kg'
      const m =
        comp.recipe_amount != null
          ? Number(comp.recipe_amount)
          : Number(comp.unit_amount || comp.quantity || 0) * n
      const unitAmount = Number(comp.unit_amount || comp.quantity || (n > 0 ? m / n : 0))

      const targetId =
        comp.component_demo_product_id ||
        comp.component_demo_semi_finished_id ||
        comp.component_product_id ||
        comp.component_semi_finished_id ||
        comp.component_raw_material_id ||
        comp.component_packaging_id ||
        comp.component_trading_good_id ||
        comp.component_id ||
        ''

      let unitCost = 0
      if (comp.component_type === 'demo_product') {
        const dp = demoProductsMap?.get(targetId)
        if (dp) {
          unitCost = calculateDemoProductTotalCost(
            dp,
            demoProductsMap || new Map(),
            demoSemiFinishedMap || new Map(),
            productsMap,
            semiFinishedMap,
            rawMaterialsMap,
            packagingsMap,
            tradingGoodsMap
          )
        }
      } else if (comp.component_type === 'demo_semi_finished') {
        const dsf = demoSemiFinishedMap?.get(targetId)
        if (dsf) {
          unitCost = calculateDemoSemiFinishedTotalCost(
            dsf,
            demoSemiFinishedMap || new Map(),
            demoProductsMap || new Map(),
            productsMap,
            semiFinishedMap,
            rawMaterialsMap,
            packagingsMap,
            tradingGoodsMap
          )
        }
      } else if (comp.component_type === 'raw_material') {
        const rm = rawMaterialsMap.get(targetId)
        unitCost = Number(rm?.maliyet ?? comp.component_cost ?? comp.birim_maliyet ?? 0)
      } else if (comp.component_type === 'packaging') {
        const pkg = packagingsMap.get(targetId)
        unitCost = Number(pkg?.maliyet ?? comp.component_cost ?? comp.birim_maliyet ?? 0)
      } else if (comp.component_type === 'trading_good') {
        const tg = tradingGoodsMap.get(targetId)
        unitCost = Number(tg?.maliyet ?? comp.component_cost ?? comp.birim_maliyet ?? 0)
      } else {
        unitCost = calculateRecursiveUnitCost(
          comp.component_type,
          targetId,
          Number(comp.component_cost ?? comp.birim_maliyet ?? 0),
          productsMap,
          semiFinishedMap,
          rawMaterialsMap,
          packagingsMap,
          tradingGoodsMap
        )
      }

      const batchCost = m * unitCost
      const unitCostTotal = unitAmount * unitCost
      const compCode = comp.component_code || comp.code || ''
      const compName = comp.component_name || comp.name || 'Bileşen'

      let targetItem: Product | SemiFinishedProduct | DemoProduct | DemoSemiFinishedProduct | RawMaterial | null = null
      let reportType: 'product' | 'semi_finished' | 'demo_product' | 'demo_semi_finished' | 'raw_material' | null = null

      if (comp.component_type === 'product') {
        targetItem = productsMap.get(targetId) || null
        reportType = 'product'
      } else if (comp.component_type === 'semi_finished') {
        targetItem = semiFinishedMap.get(targetId) || null
        reportType = 'semi_finished'
      } else if (comp.component_type === 'demo_product') {
        targetItem = demoProductsMap?.get(targetId) || null
        reportType = 'demo_product'
      } else if (comp.component_type === 'demo_semi_finished') {
        targetItem = demoSemiFinishedMap?.get(targetId) || null
        reportType = 'demo_semi_finished'
      } else if (comp.component_type === 'raw_material') {
        targetItem = rawMaterialsMap.get(targetId) || null
        reportType = 'raw_material'
      }

      return {
        comp,
        compCode,
        compName,
        compUnit,
        isKg,
        m,
        unitAmount,
        unitCost,
        batchCost,
        unitCostTotal,
        targetItem,
        reportType,
      }
    })
  }, [components, n, productsMap, semiFinishedMap, rawMaterialsMap, packagingsMap, demoProductsMap, demoSemiFinishedMap, tradingGoodsMap])

  // Sum of m values of all components with unit Kg
  const totalKgComponents = useMemo(() => {
    return componentRows
      .filter((row) => row.isKg)
      .reduce((sum, row) => sum + row.m, 0)
  }, [componentRows])

  // Sum of m values of all components
  const totalAllComponentsM = useMemo(() => {
    return componentRows.reduce((sum, row) => sum + row.m, 0)
  }, [componentRows])

  // Total batch and unit costs
  const totalBatchCost = useMemo(() => {
    return componentRows.reduce((sum, row) => sum + row.batchCost, 0)
  }, [componentRows])

  const totalUnitCost = useMemo(() => {
    return componentRows.reduce((sum, row) => sum + row.unitCostTotal, 0)
  }, [componentRows])

  const typeLabel =
    productType === 'product'
      ? 'Mamul Ürün'
      : productType === 'semi_finished'
      ? 'Yarı Mamul'
      : productType === 'demo_product'
      ? 'Demo Mamul Ürün'
      : 'Demo Yarı Mamul'

  const columns: ColumnDef[] = useMemo(() => [
    { key: 'code', label: 'Bileşen Kodu' },
    { key: 'name', label: 'Bileşen Adı' },
    { key: 'batch_amount', label: `${formatNumber(n)} ${productUnit} İçin Kullanılan Miktar` },
    { key: 'percentage', label: 'Bileşen Yüzdesi' },
    { key: 'batch_cost', label: `${formatNumber(n)} ${productUnit} İçin Kullanılan Bileşen Maliyeti` },
    { key: 'unit_amount', label: 'Birim Miktar İçin Sarfiyat' },
    { key: 'unit_cost', label: 'Birim Miktar İçin Kullanılan Bileşen Maliyeti' },
  ], [totalKgComponents])

  const categoryName = useMemo(() => {
    if (!product.category_id || !categories) return ''
    return categories.find((c) => c.id === product.category_id)?.name || ''
  }, [categories, product.category_id])

  const bomPrintRows = useMemo(() => {
    const rootCode = (product as any).code || ''
    const rootName = product.name || ''
    const rootUnit = product.unit || 'Adet'

    const rootRow: BomPrintRow = {
      id: `root-${product.id}`,
      depth: 0,
      code: rootCode,
      name: rootName,
      typeLabel: typeLabel,
      quantity: 1,
      unit: rootUnit,
      totalCost: totalUnitCost,
      isNode: true,
    }

    const rows: BomPrintRow[] = [rootRow]

    function getCompUnitCost(compType: string, targetId: string, baseCost: number): number {
      if (compType === 'demo_product') {
        const dp = demoProductsMap?.get(targetId)
        return dp
          ? calculateDemoProductTotalCost(
              dp,
              demoProductsMap || new Map(),
              demoSemiFinishedMap || new Map(),
              productsMap,
              semiFinishedMap,
              rawMaterialsMap,
              packagingsMap,
              tradingGoodsMap
            )
          : baseCost
      } else if (compType === 'demo_semi_finished') {
        const dsf = demoSemiFinishedMap?.get(targetId)
        return dsf
          ? calculateDemoSemiFinishedTotalCost(
              dsf,
              demoSemiFinishedMap || new Map(),
              demoProductsMap || new Map(),
              productsMap,
              semiFinishedMap,
              rawMaterialsMap,
              packagingsMap,
              tradingGoodsMap
            )
          : baseCost
      } else if (compType === 'raw_material') {
        const rm = rawMaterialsMap.get(targetId)
        return Number(rm?.maliyet ?? baseCost)
      } else if (compType === 'packaging') {
        const pkg = packagingsMap.get(targetId)
        return Number(pkg?.maliyet ?? baseCost)
      } else if (compType === 'trading_good') {
        const tg = tradingGoodsMap.get(targetId)
        return Number(tg?.maliyet ?? baseCost)
      } else {
        return calculateRecursiveUnitCost(
          compType as any,
          targetId,
          baseCost,
          productsMap,
          semiFinishedMap,
          rawMaterialsMap,
          packagingsMap,
          tradingGoodsMap
        )
      }
    }

    function traverse(
      compList: any[],
      parentFactor: number,
      parentOutputQty: number,
      depth: number,
      visited: Set<string>,
      path: string
    ) {
      for (let i = 0; i < compList.length; i++) {
        const comp = compList[i]
        const targetId =
          comp.component_demo_product_id ||
          comp.component_demo_semi_finished_id ||
          comp.component_product_id ||
          comp.component_semi_finished_id ||
          comp.component_raw_material_id ||
          comp.component_packaging_id ||
          comp.component_trading_good_id ||
          comp.component_id ||
          ''

        let unitAmount = Number(comp.unit_amount || 0)
        if (!unitAmount) {
          if (comp.recipe_amount != null && parentOutputQty > 0) {
            unitAmount = Number(comp.recipe_amount) / parentOutputQty
          } else {
            unitAmount = Number(comp.quantity || 0)
          }
        }

        const effectiveQty = parentFactor * unitAmount

        const baseCost = Number(comp.component_cost ?? comp.birim_maliyet ?? 0)
        const unitCost = getCompUnitCost(comp.component_type, targetId, baseCost)
        const totalCost = effectiveQty * unitCost

        const compCode = comp.component_code || comp.code || ''
        const compName = comp.component_name || comp.name || 'Bileşen'
        const compUnit = comp.component_unit || comp.unit || 'Adet'

        const isNode =
          comp.component_type === 'product' ||
          comp.component_type === 'semi_finished' ||
          comp.component_type === 'demo_product' ||
          comp.component_type === 'demo_semi_finished'

        let compTypeLabel = 'Bileşen'
        if (comp.component_type === 'product') compTypeLabel = 'Mamul'
        else if (comp.component_type === 'semi_finished') compTypeLabel = 'Yarı Mamul'
        else if (comp.component_type === 'raw_material') compTypeLabel = 'Hammadde'
        else if (comp.component_type === 'packaging') compTypeLabel = 'Ambalaj'
        else if (comp.component_type === 'demo_product') compTypeLabel = 'Demo Mamul'
        else if (comp.component_type === 'demo_semi_finished') compTypeLabel = 'Demo Yarı Mamul'
        else if (comp.component_type === 'trading_good') compTypeLabel = 'Al-Sat'

        const rowId = `${path}-${depth}-${i}-${targetId}`

        rows.push({
          id: rowId,
          depth,
          code: compCode,
          name: compName,
          typeLabel: compTypeLabel,
          quantity: effectiveQty,
          unit: compUnit,
          totalCost,
          isNode,
        })

        if (isNode && targetId && !visited.has(targetId)) {
          let subItem: any = null
          if (comp.component_type === 'product') subItem = productsMap.get(targetId)
          else if (comp.component_type === 'semi_finished') subItem = semiFinishedMap.get(targetId)
          else if (comp.component_type === 'demo_product') subItem = demoProductsMap?.get(targetId)
          else if (comp.component_type === 'demo_semi_finished') subItem = demoSemiFinishedMap?.get(targetId)

          if (subItem && subItem.components && subItem.components.length > 0) {
            const nextVisited = new Set(visited)
            nextVisited.add(targetId)
            const subOutputQty = Number(subItem.recipe_output_quantity || 1)
            traverse(subItem.components, effectiveQty, subOutputQty, depth + 1, nextVisited, rowId)
          }
        }
      }
    }

    traverse(components, 1, n, 1, new Set([product.id]), 'root')

    return rows
  }, [
    product,
    components,
    n,
    typeLabel,
    totalUnitCost,
    productsMap,
    semiFinishedMap,
    rawMaterialsMap,
    packagingsMap,
    demoProductsMap,
    demoSemiFinishedMap,
    tradingGoodsMap,
  ])

  const isColVisible = (key: string) => !hiddenColumns[key]

  const handleToggleColumn = (key: string) => {
    setHiddenColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handleShowAllColumns = () => {
    setHiddenColumns({})
  }

  const handleHideAllColumns = () => {
    const allHidden: Record<string, boolean> = {}
    columns.forEach((col) => {
      allHidden[col.key] = true
    })
    setHiddenColumns(allHidden)
  }

  return (
    <div className="view-container">
      {/* Top Header / Breadcrumb Actions */}
      <div className="section-header-flex no-print" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onBack}
            title="Geri Dön"
          >
            <ArrowLeftIcon />
            Geri Dön
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>{product.name}</span>
            </h2>
            {(product as any).code ? (
              <code className="badge badge-secondary" style={{ fontSize: '0.85rem' }}>
                {(product as any).code}
              </code>
            ) : (
              <span className="badge badge-secondary" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                DEMO
              </span>
            )}
            <span
              className="badge"
              style={{
                fontSize: '0.82rem',
                backgroundColor:
                  productType === 'demo_product'
                    ? '#6366f1'
                    : productType === 'demo_semi_finished'
                    ? '#8b5cf6'
                    : 'var(--color-primary, #2563eb)',
                color: '#fff',
              }}
            >
              {typeLabel}
            </span>
          </div>
        </div>

        <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => window.print()}
            title="BoM Raporunu Yazdır / PDF Olarak Kaydet"
          >
            <PrinterIcon />
            <span>Yazdır</span>
          </button>
          <div className="badge badge-outline" style={{ padding: '0.45rem 0.85rem', fontSize: '0.85rem' }}>
            Reçete Çıktısı: <strong>{formatNumber(n)} {productUnit}</strong>
          </div>
        </div>
      </div>

      {/* Report Tabs (Right below the top area) */}
      <div className="report-tab-nav no-print">
        <button
          type="button"
          className={`report-tab-btn ${activeTab === 'recipe' ? 'active' : ''}`}
          onClick={() => setActiveTab('recipe')}
        >
          <PieChartIcon />
          Reçete Raporu
        </button>
      </div>

      {/* Reçete Raporu Tab Content */}
      {activeTab === 'recipe' && (
        <div className="no-print">
          {/* Summary Label Banner */}
          <div className="report-summary-banner">
            <div className="report-summary-text">
              <strong>{formatNumber(n)} {productUnit}</strong> ürün için{' '}
              <strong style={{ color: 'var(--color-primary)' }}>
                {formatNumber(totalKgComponents)} Kg
              </strong>{' '}
              bileşen kullanıldı.
            </div>
            {components.length > 0 && (
              <div className="report-summary-subtext">
                Toplam {components.length} bileşen girdisi • Birim Reçete Maliyeti: ₺{formatCurrency(totalUnitCost)}
              </div>
            )}
          </div>

          {/* Table Container Styled Like Price List */}
          {components.length === 0 ? (
            <div className="card empty-state" style={{ padding: '3.5rem 1.5rem', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '0.95rem' }}>Bu ürüne ait henüz bir reçete bileşeni bulunmuyor.</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onBack}
                style={{ marginTop: '1rem' }}
              >
                Geri Dön ve Bileşen Ekle
              </button>
            </div>
          ) : (
            <div
              className="card table-card"
              style={{
                marginTop: '1rem',
                overflowX: 'auto',
                overflowY: 'hidden',
                maxWidth: '100%',
              }}
            >
              <table
                className="data-table price-list-table report-table"
                style={{ width: '100%', minWidth: '920px' }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        width: '38px',
                        minWidth: '38px',
                        textAlign: 'center',
                        verticalAlign: 'bottom',
                        padding: '0.45rem 0.35rem',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setShowColumnModal(true)}
                        title="Sütun Görünürlüğü"
                        aria-label="Sütun görünürlüğünü ayarla"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '3px',
                          borderRadius: '4px',
                          color: '#6b7280',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1,
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#111827'
                          e.currentTarget.style.background = '#e5e7eb'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#6b7280'
                          e.currentTarget.style.background = 'transparent'
                        }}
                      >
                        <GearIcon size={14} />
                      </button>
                    </th>
                    {isColVisible('code') && (
                      <th style={{ minWidth: '120px' }}>
                        Bileşen<br />Kodu
                      </th>
                    )}
                    {isColVisible('name') && (
                      <th style={{ minWidth: '180px' }}>
                        Bileşen<br />Adı
                      </th>
                    )}
                    {isColVisible('batch_amount') && (
                      <th style={{ minWidth: '160px', textAlign: 'right' }}>
                        {formatNumber(n)} {productUnit} İçin<br />Kullanılan Miktar
                      </th>
                    )}
                    {isColVisible('percentage') && (
                      <th style={{ minWidth: '105px', textAlign: 'right' }}>
                        Bileşen<br />Yüzdesi
                      </th>
                    )}
                    {isColVisible('batch_cost') && (
                      <th style={{ minWidth: '170px', textAlign: 'right' }}>
                        {formatNumber(n)} {productUnit} İçin<br />Kullanılan Bileşen Maliyeti
                      </th>
                    )}
                    {isColVisible('unit_amount') && (
                      <th style={{ minWidth: '140px', textAlign: 'right' }}>
                        Birim Miktar İçin<br />Sarfiyat
                      </th>
                    )}
                    {isColVisible('unit_cost') && (
                      <th style={{ minWidth: '170px', textAlign: 'right' }}>
                        Birim Miktar İçin<br />Kullanılan Bileşen Maliyeti
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {componentRows.map(({ comp, compCode, compName, compUnit, m, unitAmount, batchCost, unitCostTotal, targetItem, reportType }, idx) => {
                    const percentage = totalAllComponentsM > 0 ? (m / totalAllComponentsM) * 100 : 0
                    const compTypeBadge =
                      comp.component_type === 'raw_material'
                        ? 'Hammadde'
                        : comp.component_type === 'packaging'
                        ? 'Ambalaj'
                        : comp.component_type === 'trading_good'
                        ? 'Al-Sat'
                        : comp.component_type === 'semi_finished'
                        ? 'Yarı Mamul'
                        : comp.component_type === 'product'
                        ? 'Mamul Ürün'
                        : comp.component_type === 'demo_product'
                        ? 'Demo Mamul'
                        : 'Demo Yarı Mamul'

                    const badgeColor =
                      comp.component_type === 'demo_product'
                        ? '#6366f1'
                        : comp.component_type === 'demo_semi_finished'
                        ? '#8b5cf6'
                        : comp.component_type === 'trading_good'
                        ? '#6f42c1'
                        : undefined

                    return (
                      <tr key={comp.id || idx}>
                        <td style={{ textAlign: 'center', padding: '0.35rem 0.25rem', verticalAlign: 'middle' }}>
                          {targetItem && reportType && (() => {
                            if (reportType === 'raw_material') {
                              return (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => onOpenReport?.(targetItem, reportType)}
                                  title="Hammadde Raporu"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '3px 5px',
                                    lineHeight: 1,
                                  }}
                                >
                                  <PieChartIcon size={13} style={{ marginRight: 0 }} />
                                </button>
                              )
                            }
                            if (
                              (reportType === 'semi_finished' || reportType === 'demo_semi_finished') &&
                              isSemiFinishedEligibleForRecipeReport(targetItem as any)
                            ) {
                              return (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => onOpenReport?.(targetItem, reportType)}
                                  title="Reçete Raporu"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '3px 5px',
                                    lineHeight: 1,
                                  }}
                                >
                                  <PieChartIcon size={13} style={{ marginRight: 0 }} />
                                </button>
                              )
                            }
                            return null
                          })()}
                        </td>
                        {isColVisible('code') && (
                          <td>
                            {compCode ? (
                              <code className="bom-code-badge">{compCode}</code>
                            ) : (
                              <code className="bom-code-badge" style={{ fontStyle: 'italic', opacity: 0.85 }}>DEMO</code>
                            )}
                          </td>
                        )}
                        {isColVisible('name') && (
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                              <span style={{ fontWeight: 500 }}>{compName}</span>
                              <span
                                className={`badge badge-${
                                  comp.component_type === 'raw_material'
                                    ? 'success'
                                    : comp.component_type === 'packaging'
                                    ? 'warning'
                                    : comp.component_type === 'semi_finished'
                                    ? 'primary'
                                    : comp.component_type === 'product'
                                    ? 'secondary'
                                    : 'secondary'
                                }`}
                                style={{
                                  fontSize: '0.7rem',
                                  padding: '1px 5px',
                                  backgroundColor: badgeColor,
                                  color: badgeColor ? '#fff' : undefined,
                                }}
                              >
                                {compTypeBadge}
                              </span>
                            </div>
                          </td>
                        )}
                        {isColVisible('batch_amount') && (
                          <td className="tabular-nums" style={{ textAlign: 'right' }}>
                            <strong>{formatNumber(m)}</strong>{' '}
                            <span className="text-muted" style={{ fontSize: '0.78rem' }}>{compUnit}</span>
                          </td>
                        )}
                        {isColVisible('percentage') && (
                          <td className="tabular-nums" style={{ textAlign: 'right' }}>
                            <strong style={{ color: 'var(--color-primary)' }}>{formatPercent(percentage)}</strong>
                          </td>
                        )}
                        {isColVisible('batch_cost') && (
                          <td className="tabular-nums col-cost" style={{ textAlign: 'right' }}>
                            ₺{formatCurrency(batchCost)}
                          </td>
                        )}
                        {isColVisible('unit_amount') && (
                          <td className="tabular-nums" style={{ textAlign: 'right' }}>
                            <strong>{formatNumber(unitAmount)}</strong>{' '}
                            <span className="text-muted" style={{ fontSize: '0.78rem' }}>{compUnit}</span>
                          </td>
                        )}
                        {isColVisible('unit_cost') && (
                          <td className="tabular-nums col-cost" style={{ textAlign: 'right' }}>
                            ₺{formatCurrency(unitCostTotal)}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="report-table-footer">
                    <td style={{ textAlign: 'center', color: '#9ca3af' }}>•</td>
                    {isColVisible('code') && (
                      <td>
                        <strong>Genel Toplam</strong>
                      </td>
                    )}
                    {isColVisible('name') && (
                      <td>
                        {!isColVisible('code') ? <strong>Genel Toplam</strong> : null}
                      </td>
                    )}
                    {isColVisible('batch_amount') && (
                      <td className="tabular-nums" style={{ textAlign: 'right' }}>
                        <strong>{formatNumber(totalKgComponents)} Kg</strong>
                      </td>
                    )}
                    {isColVisible('percentage') && (
                      <td className="tabular-nums" style={{ textAlign: 'right' }}>
                        <strong>{totalAllComponentsM > 0 ? '%100,00' : '%0,00'}</strong>
                      </td>
                    )}
                    {isColVisible('batch_cost') && (
                      <td className="tabular-nums col-cost" style={{ textAlign: 'right' }}>
                        <strong>₺{formatCurrency(totalBatchCost)}</strong>
                      </td>
                    )}
                    {isColVisible('unit_amount') && (
                      <td className="tabular-nums" style={{ textAlign: 'right' }}>
                        <strong>{n > 0 ? formatNumber(totalKgComponents / n) : '0'} Kg</strong>
                      </td>
                    )}
                    {isColVisible('unit_cost') && (
                      <td className="tabular-nums col-cost" style={{ textAlign: 'right' }}>
                        <strong>₺{formatCurrency(totalUnitCost)}</strong>
                      </td>
                    )}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Column Visibility Modal */}
      {showColumnModal && (
        <div className="modal-backdrop" onClick={() => setShowColumnModal(false)}>
          <div
            className="modal-card"
            style={{ maxWidth: '440px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Sütun Görünürlüğü</h3>
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowColumnModal(false)}
                aria-label="Kapat"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  color: 'var(--text-muted, #6b7280)',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            </div>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              Reçete raporu tablosunda görüntülemek istediğiniz sütunları işaretleyin.
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleShowAllColumns}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
              >
                Tümünü Seç
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleHideAllColumns}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
              >
                Tümünü Kaldır
              </button>
            </div>

            <div
              style={{
                maxHeight: '340px',
                overflowY: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              {columns.map((col) => {
                const checked = isColVisible(col.key)
                return (
                  <label
                    key={col.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '0.45rem 0.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: checked ? '#f3f4f6' : 'transparent',
                      fontSize: '0.85rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleColumn(col.key)}
                    />
                    <span>{col.label}</span>
                  </label>
                )
              })}
            </div>

            <div className="modal-actions" style={{ marginTop: '1rem', paddingTop: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowColumnModal(false)}
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Printable Document (Visible only when printing) */}
      <div className="bom-print-document">
        <div className="bom-print-header">
          <div className="bom-print-header-left">
            <div className="bom-print-product-info">
              {(product as any).code && (
                <span className="bom-print-code">{(product as any).code}</span>
              )}
              <span className="bom-print-name">{product.name}</span>
              <span className="bom-print-type-badge">{typeLabel}</span>
            </div>
            <div className="bom-print-meta-sub">
              <span>Reçete Çıktısı: <strong>{formatNumber(n)} {productUnit}</strong></span>
              {categoryName && (
                <span>
                  {' • '}Kategori: <strong>{categoryName}</strong>
                </span>
              )}
            </div>
          </div>
          <div className="bom-print-header-right">
            <div className="bom-print-date">Tarih: {new Date().toLocaleDateString('tr-TR')}</div>
          </div>
        </div>

        <table className="bom-print-table">
          <thead>
            <tr>
              <th className="bom-col-product">BİLEŞEN / ÜRÜN</th>
              <th className="bom-col-quantity">BİRİM MİKTAR</th>
              <th className="bom-col-cost">TOPLAM MALİYET</th>
            </tr>
          </thead>
          <tbody>
            {bomPrintRows.map((row) => (
              <tr key={row.id} className={row.isNode ? 'bom-row-node' : 'bom-row-leaf'}>
                <td className="bom-col-product">
                  <div
                    className="bom-cell-item"
                    style={{ paddingLeft: `${row.depth * 28}px` }}
                  >
                    {row.code && <span className="bom-item-code">{row.code}</span>}
                    <span className="bom-item-name">{row.name}</span>
                    <span className="bom-item-type">{row.typeLabel}</span>
                  </div>
                </td>
                <td className="bom-col-quantity tabular-nums">
                  {formatPrintQuantity(row.quantity)} {row.unit}
                </td>
                <td className="bom-col-cost tabular-nums">
                  ₺{formatCurrency(row.totalCost)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bom-print-footer-row">
              <td colSpan={2} className="bom-footer-label">
                Birim Reçete Maliyeti ({productUnit} Başına)
              </td>
              <td className="bom-footer-value tabular-nums">
                ₺{formatCurrency(totalUnitCost)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}
